import { Menu } from "@headlessui/react";
import clsx from "clsx";
import { Link } from "react-router-dom";

import { ChevronDown } from "@/assets/icons/ds";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";

export interface StepMenuItem {
  label: string;
  /** Route to jump straight to this step - renders the item as a real link. */
  path?: string;
  /** Use with `onSelect` instead of `path` for in-memory step state (SlideStepper). */
  disabled?: boolean;
}

export interface StepMenuProps {
  items: StepMenuItem[];
  /** 0-based index of the step currently shown. */
  currentIndex: number;
  /** Only needed for items without a `path`. */
  onSelect?: (index: number) => void;
  /** Accessible name of the chevron toggle button. */
  toggleLabel: string;
  /**
   * When set, renders the step counter (e.g. "02 / 02") inside the toggle
   * button so clicking the counter also opens the dropdown.
   */
  counterLabel?: string;
  /**
   * `"compact"` (default): SlideStepper's small unnumbered list on a
   * bordered dark panel, all items full-opacity.
   * `"stepper"`: the wizard-stepper's roomier numbered list on a light
   * surface - design-system node 2648:3908 ("Progress Bar Steps with
   * Dropdown"). The current item is full-opacity, the rest are dimmed.
   */
  variant?: "compact" | "stepper";
  className?: string;
}

/**
 * Chevron button that opens a dropdown to jump straight to any step of a
 * flow - the design system's `dire-dropdown` of `dire-menu-item`s.
 */
export const StepMenu = ({
  items,
  currentIndex,
  onSelect,
  toggleLabel,
  counterLabel,
  variant = "compact",
  className,
}: StepMenuProps) => {
  const stepper = variant === "stepper";

  return (
    <Menu as="div" className={clsx("relative", className)}>
      <Menu.Button
        className={clsx(
          "group relative flex cursor-pointer items-center gap-1 rounded-md focus:outline-none",
          stepper
            ? "text-foreground-primary"
            : "text-foreground-primary-inverse",
        )}
        aria-label={toggleLabel}
      >
        {counterLabel && (
          <span className="text-body-sm leading-none tracking-[0.28px]">
            {counterLabel}
          </span>
        )}
        <span className="relative flex size-8 items-center justify-center">
          <ChevronDown className="size-4" />
          <FocusRing inverted={!stepper} />
        </span>
      </Menu.Button>
      <Menu.Items
        className={clsx(
          "absolute right-0 z-10 flex w-fit flex-col bg-surface-primary focus:outline-none",
          stepper
            ? "top-9 gap-1 rounded-lg p-2"
            : "top-8 rounded-md border border-border-tertiary-inverse p-1",
        )}
      >
        {items.map((item, index) => {
          const current = index === currentIndex;
          const itemClassName = clsx(
            "flex items-center gap-2 whitespace-nowrap rounded-sm text-left text-foreground-primary-inverse",
            stepper
              ? "rounded-md px-3 py-2.5 text-body-base leading-none"
              : "px-2 py-1 text-body-sm leading-md",
            current
              ? "font-semibold"
              : stepper && "text-foreground-tertiary-inverse",
          );

          return (
            <Menu.Item key={item.label} disabled={item.disabled}>
              {({ active }) => {
                const content = (
                  <>
                    {stepper && (
                      // Trailing space keeps the accessible name "1 Abo
                      // Auswahl", not "1Abo Auswahl" - the gap-2 between the
                      // two spans is visual only, text content concatenates
                      // regardless of it.
                      <span className="tabular-nums">{index + 1} </span>
                    )}
                    <span className="truncate">{item.label}</span>
                  </>
                );
                const resolvedClassName = clsx(
                  itemClassName,
                  active && !item.disabled && "bg-surface-tertiary-inverse",
                  item.disabled && "cursor-not-allowed",
                );

                if (item.disabled) {
                  return (
                    <div className={resolvedClassName} aria-disabled="true">
                      {content}
                    </div>
                  );
                }

                if (item.path) {
                  return (
                    <Link to={item.path} className={resolvedClassName}>
                      {content}
                    </Link>
                  );
                }

                return (
                  <button
                    type="button"
                    className={resolvedClassName}
                    onClick={() => onSelect?.(index)}
                  >
                    {content}
                  </button>
                );
              }}
            </Menu.Item>
          );
        })}
      </Menu.Items>
    </Menu>
  );
};
